import React from 'react';
import { useContactForm } from '../../hooks/useContactForm';
import './ContactForm.css';

/**
 * Componente ContactForm - Formulario de contacto funcional
 * El envío lo resuelve useContactForm (EmailService → Resend)
 */
// Familiar NO se ofrece: deshabilitada (FUNC-131).
const SERVICE_OPTIONS = [
  'Pareja',
  'Pareja a domicilio',
  'Individual',
  'Sexología',
  'Online',
  'Aún no lo sé',
];

interface ContactFormProps {
  /** Identifica el formulario en el correo que recibe la clínica. */
  origin?: string;
  /** Bloque "Contacto rápido" (WhatsApp/Llamar) bajo el formulario. */
  showQuickContact?: boolean;
}

const ContactForm: React.FC<ContactFormProps> = ({
  origin = 'Formulario de contacto principal',
  showQuickContact = true,
}) => {
  const {
    register,
    handleSubmit,
    errors,
    isSubmitting,
    submitStatus,
    clearMessage
  } = useContactForm(origin);

  return (
    <div className="contact-form animate-in" style={{ '--delay': '0.4s' } as React.CSSProperties}>
      <form onSubmit={handleSubmit} id="contact-form">
        {/* Campo Nombre */}
        <div className="form-group">
          <label htmlFor="contact-name">Nombre</label>
          <input
            type="text"
            id="contact-name"
            placeholder="Escribe tu nombre completo"
            className={`form-control ${errors.name ? 'error' : ''}`}
            {...register('name', {
              required: 'Este campo es obligatorio',
              minLength: {
                value: 2,
                message: 'El nombre debe tener al menos 2 caracteres'
              }
            })}
          />
          <div className="form-highlight"></div>
          {errors.name && (
            <span className="error-message">
              <i className="fas fa-exclamation-circle"></i>
              {errors.name.message}
            </span>
          )}
        </div>

        {/* Campo Email */}
        <div className="form-group">
          <label htmlFor="contact-email">Correo electrónico</label>
          <input
            type="email"
            id="contact-email"
            placeholder="ejemplo@correo.com"
            autoComplete="email"
            className={`form-control ${errors.email ? 'error' : ''}`}
            {...register('email', {
              required: 'Este campo es obligatorio',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Por favor ingresa un correo electrónico válido'
              }
            })}
          />
          <div className="form-highlight"></div>
          {errors.email && (
            <span className="error-message">
              <i className="fas fa-exclamation-circle"></i>
              {errors.email.message}
            </span>
          )}
        </div>

        {/* Campo Teléfono */}
        <div className="form-group">
          <label htmlFor="phone">Celular</label>
          <input
            type="tel"
            id="phone"
            placeholder="+56 9 1234 5678"
            className={`form-control ${errors.phone ? 'error' : ''}`}
            {...register('phone', {
              required: 'Este campo es obligatorio',
              minLength: {
                value: 8,
                message: 'El número debe tener al menos 8 dígitos'
              }
            })}
          />
          <div className="form-highlight"></div>
          {errors.phone && (
            <span className="error-message">
              <i className="fas fa-exclamation-circle"></i>
              {errors.phone.message}
            </span>
          )}
        </div>

        {/* Servicio de interés — select, NO texto libre: no pedimos datos de
            salud por formulario (wisest-labs/01 §8.2 C-4). */}
        <div className="form-group">
          {/* Chips (radio) en vez de <select>: una línea, un clic, sin dropdown
              nativo (el .form-group select global del portal lo inflaba). */}
          <fieldset className="form-chips">
            <legend>¿Qué te interesa?</legend>
            <div className="form-chips__list">
              {SERVICE_OPTIONS.map((opt) => (
                <label key={opt} className="form-chip">
                  <input
                    type="radio"
                    value={opt}
                    {...register('reason', { required: 'Elige una opción' })}
                  />
                  <span>{opt}</span>
                </label>
              ))}
            </div>
          </fieldset>
          {errors.reason && (
            <span className="error-message">
              <i className="fas fa-exclamation-circle"></i>
              {errors.reason.message}
            </span>
          )}
        </div>

        {/* Consentimiento — obligatorio, no premarcado */}
        <div className="form-group form-consent">
          <label htmlFor="contact-consent" className="form-consent__label">
            <input
              type="checkbox"
              id="contact-consent"
              {...register('consent', { required: 'Debes aceptar para continuar' })}
            />
            <span>
              Acepto la{' '}
              <a href="/privacidad" target="_blank" rel="noopener noreferrer">política de privacidad</a>{' '}
              y que me contacten para coordinar mi atención.
            </span>
          </label>
          {errors.consent && (
            <span className="error-message">
              <i className="fas fa-exclamation-circle"></i>
              {errors.consent.message}
            </span>
          )}
        </div>

        {/* Botón de envío */}
        <button
          type="submit"
          className="btn btn-primary btn-block"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <i className="fas fa-spinner fa-spin"></i>
              <span>Enviando...</span>
            </>
          ) : (
            <>
              <span>Quiero que me contacten</span>
              <i className="fas fa-paper-plane"></i>
            </>
          )}
        </button>

        {/* Mensaje de estado */}
        {submitStatus.type && (
          <div className={`form-feedback ${submitStatus.type}`}>
            <button
              type="button"
              className="close-message"
              onClick={clearMessage}
              aria-label="Cerrar mensaje"
            >
              <i className="fas fa-times"></i>
            </button>
            <div className="message-content">
              <i className={`fas fa-${submitStatus.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
              <span>{submitStatus.message}</span>
            </div>
          </div>
        )}
      </form>

      {/* Contacto rápido */}
      {showQuickContact && (
      <div className="quick-contact">
        <h3>Contacto Rápido</h3>
        <p>También puedes contactarnos directamente:</p>

        <div className="quick-links">
          <a
            href="https://wa.me/56978009119?text=Hola,%20me%20gustaría%20agendar%20una%20consulta%20en%20Conectemos%20Chile"
            className="btn btn-secondary"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="fab fa-whatsapp"></i>
            <span>WhatsApp</span>
          </a>

          <a href="tel:+56978009119" className="btn btn-secondary">
            <i className="fas fa-phone"></i>
            <span>Llamar</span>
          </a>
        </div>
      </div>
      )}
    </div>
  );
};

export default ContactForm;